import { Component, DestroyRef, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_DISAVOW_KINDS,
  SEARCH_OPS_OUTREACH_CHANNELS,
  SEARCH_OPS_OUTREACH_RESULTS,
  SEARCH_OPS_PROSPECT_STATUSES,
  SearchBacklinkDto,
  SearchBacklinkImportResultDto,
  SearchDisavowItemDto,
  SearchLinkProspectDto,
  SearchOutreachDto,
  SearchOutreachFunnelBucketDto,
  SearchPropertyListItemDto,
  searchOpsOffpageEvidenceFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-offpage',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    RouterLink,
    JalaliDateInputComponent,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.offpage.title')"
      [description]="t('growth.searchops.offpage.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.offpage.help.title'"
        [descriptionKey]="'growth.searchops.offpage.help.description'"
        [bulletKeys]="[
          'growth.searchops.offpage.help.bullets.score',
          'growth.searchops.offpage.help.bullets.email',
          'growth.searchops.offpage.help.bullets.buy'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/local']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.local')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/sem']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.sem')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.offpage.sections.funnel') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.offpage.funnelHelp.title'"
              [descriptionKey]="'growth.searchops.offpage.funnelHelp.description'" />
          </div>
          <p-table [value]="funnel()" [tableStyle]="{ 'min-width': '20rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.result') }}</th>
                <th>{{ t('growth.searchops.fields.count') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ t('growth.searchops.outreachResult.' + row.result) }}</td>
                <td>{{ row.count }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.offpage.sections.prospects') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.offpage.prospectsHelp.title'"
              [descriptionKey]="'growth.searchops.offpage.prospectsHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.offpage.actions.addProspect')" (click)="openProspect()"></button>
          <p-table [value]="prospects()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '48rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.domain') }}</th>
                <th>{{ t('growth.searchops.fields.score') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.contact') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.domain }}</td>
                <td>{{ row.combinedScore }}</td>
                <td>{{ t('growth.searchops.prospectStatus.' + row.status) }}</td>
                <td>{{ contactLabel(row) }}</td>
                <td>
                  <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.offpage.actions.createTask')" (click)="createProspectTask(row)"></button>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">{{ t('growth.searchops.offpage.emptyProspects') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.offpage.sections.outreach') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.offpage.outreachHelp.title'"
              [descriptionKey]="'growth.searchops.offpage.outreachHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.offpage.actions.addOutreach')" (click)="openOutreach()"></button>
          <p-table [value]="outreach()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '40rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.prospect') }}</th>
                <th>{{ t('growth.searchops.fields.channel') }}</th>
                <th>{{ t('growth.searchops.fields.result') }}</th>
                <th>{{ t('growth.searchops.fields.lastTouch') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.prospectDomain }}</td>
                <td>{{ t('growth.searchops.outreachChannel.' + row.channel) }}</td>
                <td>{{ t('growth.searchops.outreachResult.' + row.result) }}</td>
                <td>{{ row.lastTouchAtUtc }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.offpage.emptyOutreach') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.offpage.sections.backlinks') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.offpage.backlinksHelp.title'"
              [descriptionKey]="'growth.searchops.offpage.backlinksHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.offpage.actions.importCsv')" (click)="openCsvPicker()"></button>
          <input #csvFileInput type="file" accept=".csv,text/csv" hidden (change)="onCsvSelected($event)" />
          @if (importSummary()) {
            <p>{{ importSummary() }}</p>
          }
          <p-table [value]="backlinks()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '48rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.sourceUrl') }}</th>
                <th>{{ t('growth.searchops.fields.targetUrl') }}</th>
                <th>{{ t('growth.searchops.fields.rel') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.acquiredVia') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.sourceUrl }}</td>
                <td>{{ row.targetUrl }}</td>
                <td>{{ t('growth.searchops.backlinkRel.' + row.rel) }}</td>
                <td>{{ t('growth.searchops.backlinkStatus.' + row.status) }}</td>
                <td>{{ t('growth.searchops.backlinkAcquired.' + row.acquiredVia) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">{{ t('growth.searchops.offpage.emptyBacklinks') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.offpage.sections.disavow') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.offpage.disavowHelp.title'"
              [descriptionKey]="'growth.searchops.offpage.disavowHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.offpage.actions.addDisavow')" (click)="openDisavow()"></button>
          <p-table [value]="disavow()" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.entity') }}</th>
                <th>{{ t('growth.searchops.fields.reason') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.entity }}</td>
                <td>{{ row.reason }}</td>
                <td>{{ t('growth.searchops.disavowStatus.' + row.status) }}</td>
                <td>
                  @if (canApply() && row.status === 'draft') {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.offpage.actions.submitDisavow')" (click)="submitDisavow(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.offpage.emptyDisavow') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>

    <p-dialog [(visible)]="prospectOpen" [modal]="true" [header]="t('growth.searchops.offpage.dialogs.prospect')" [style]="{ width: '32rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.offpage.prospectDialogHelp.title'"
        [descriptionKey]="'growth.searchops.offpage.prospectDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.domain')">
        <input pInputText [(ngModel)]="prospectModel.domain" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.targetUrl')">
        <input pInputText [(ngModel)]="prospectModel.targetUrl" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.contactEmail')">
        <input pInputText [(ngModel)]="prospectModel.contactEmail" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.contactPhone')">
        <input pInputText [(ngModel)]="prospectModel.contactPhone" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.status')">
        <p-select [options]="prospectStatusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="prospectModel.status" appendTo="body" />
      </ain-form-field>
      <ain-structured-json-form [fields]="evidenceFields" [(payloadJson)]="prospectModel.evidenceJson" />
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveProspect()"></button>
      </div>
    </p-dialog>

    <p-dialog [(visible)]="outreachOpen" [modal]="true" [header]="t('growth.searchops.offpage.dialogs.outreach')" [style]="{ width: '32rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.offpage.outreachDialogHelp.title'"
        [descriptionKey]="'growth.searchops.offpage.outreachDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.prospect')">
        <p-select [options]="prospectPicker()" optionLabel="label" optionValue="value" [(ngModel)]="outreachModel.prospectId" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.channel')">
        <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="outreachModel.channel" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.result')">
        <p-select [options]="resultOptions()" optionLabel="label" optionValue="value" [(ngModel)]="outreachModel.result" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.lastTouch')">
        <ain-date-input [(ngModel)]="outreachModel.lastTouchAtUtc" />
      </ain-form-field>
      <ain-structured-json-form [fields]="evidenceFields" [(payloadJson)]="outreachModel.evidenceJson" />
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveOutreach()"></button>
      </div>
    </p-dialog>

    <p-dialog [(visible)]="disavowOpen" [modal]="true" [header]="t('growth.searchops.offpage.dialogs.disavow')" [style]="{ width: '28rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.offpage.disavowDialogHelp.title'"
        [descriptionKey]="'growth.searchops.offpage.disavowDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.entity')">
        <input pInputText [(ngModel)]="disavowModel.entity" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.entityKind')">
        <p-select [options]="kindOptions()" optionLabel="label" optionValue="value" [(ngModel)]="disavowModel.entityKind" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.reason')">
        <input pInputText [(ngModel)]="disavowModel.reason" />
      </ain-form-field>
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveDisavow()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .toolbar, .section-title, .dialog-actions { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; margin-bottom: 1rem; }
    .live-badge { margin-inline-start: auto; }
    .card { margin-bottom: 1.25rem; }
    h3 { margin: 0; }
    td { white-space: normal; overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsOffpageComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly csvFileInput = viewChild<ElementRef<HTMLInputElement>>('csvFileInput');
  readonly evidenceFields = searchOpsOffpageEvidenceFields;
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly prospects = signal<SearchLinkProspectDto[]>([]);
  readonly outreach = signal<SearchOutreachDto[]>([]);
  readonly funnel = signal<SearchOutreachFunnelBucketDto[]>([]);
  readonly backlinks = signal<SearchBacklinkDto[]>([]);
  readonly disavow = signal<SearchDisavowItemDto[]>([]);
  readonly importSummary = signal<string | null>(null);
  selectedPropertyId: string | null = null;
  prospectOpen = false;
  outreachOpen = false;
  disavowOpen = false;
  prospectModel = {
    domain: '',
    targetUrl: '',
    contactEmail: '',
    contactPhone: '',
    status: 'identified',
    evidenceJson: '{}'
  };
  outreachModel = {
    prospectId: '',
    channel: 'email',
    result: 'drafted',
    lastTouchAtUtc: '',
    evidenceJson: '{}'
  };
  disavowModel = { entity: '', entityKind: 'domain', reason: '' };
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.growthSearchOpsChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  canApply(): boolean {
    const ctx = this.context.context();
    if (ctx?.roles?.includes('SystemAdmin') || ctx?.isPlatformAdmin) {
      return true;
    }
    return (ctx?.permissions ?? []).some(item => item.key === 'growth.searchops.apply');
  }

  canSeePii(): boolean {
    const ctx = this.context.context();
    if (ctx?.roles?.includes('SystemAdmin') || ctx?.isPlatformAdmin) {
      return true;
    }
    return (ctx?.permissions ?? []).some(item => item.key === 'growth.searchops.outreach.pii');
  }

  contactLabel(row: SearchLinkProspectDto): string {
    if (row.contactMasked || !this.canSeePii()) {
      return this.t('growth.searchops.offpage.contactHidden');
    }
    return row.contactEmail || row.contactPhone || this.t('growth.searchops.offpage.contactNone');
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  prospectPicker() {
    return this.prospects().map(item => ({ value: item.id, label: item.domain }));
  }

  prospectStatusOptions() {
    return SEARCH_OPS_PROSPECT_STATUSES.map(value => ({ value, label: this.t('growth.searchops.prospectStatus.' + value) }));
  }

  channelOptions() {
    return SEARCH_OPS_OUTREACH_CHANNELS.map(value => ({ value, label: this.t('growth.searchops.outreachChannel.' + value) }));
  }

  resultOptions() {
    return SEARCH_OPS_OUTREACH_RESULTS.map(value => ({ value, label: this.t('growth.searchops.outreachResult.' + value) }));
  }

  kindOptions() {
    return SEARCH_OPS_DISAVOW_KINDS.map(value => ({ value, label: this.t('growth.searchops.disavowKind.' + value) }));
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  openProspect(): void {
    this.prospectModel = {
      domain: '',
      targetUrl: '',
      contactEmail: '',
      contactPhone: '',
      status: 'identified',
      evidenceJson: '{}'
    };
    this.prospectOpen = true;
  }

  openOutreach(): void {
    this.outreachModel = {
      prospectId: this.prospects()[0]?.id ?? '',
      channel: 'email',
      result: 'drafted',
      lastTouchAtUtc: new Date().toISOString(),
      evidenceJson: '{}'
    };
    this.outreachOpen = true;
  }

  openDisavow(): void {
    this.disavowModel = { entity: '', entityKind: 'domain', reason: '' };
    this.disavowOpen = true;
  }

  openCsvPicker(): void {
    this.csvFileInput()?.nativeElement.click();
  }

  onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.selectedPropertyId) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.busy.set(true);
      this.api.importBacklinks(this.selectedPropertyId!, String(reader.result ?? '')).pipe(
        finalize(() => this.busy.set(false))
      ).subscribe({
        next: (result: SearchBacklinkImportResultDto) => {
          this.importSummary.set(
            this.t('growth.searchops.offpage.importResult')
              .replace('{accepted}', String(result.accepted))
              .replace('{rejected}', String(result.rejected))
          );
          this.load(false);
        },
        error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
      });
    };
    reader.readAsText(file);
  }

  saveProspect(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertProspect({
      searchPropertyId: this.selectedPropertyId,
      domain: this.prospectModel.domain,
      targetUrl: this.prospectModel.targetUrl || null,
      contactEmail: this.prospectModel.contactEmail || null,
      contactPhone: this.prospectModel.contactPhone || null,
      status: this.prospectModel.status,
      doNotContact: this.prospectModel.status === 'do_not_contact',
      evidenceJson: this.prospectModel.evidenceJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.prospectOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveOutreach(): void {
    if (!this.selectedPropertyId || !this.outreachModel.prospectId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertOutreach({
      searchPropertyId: this.selectedPropertyId,
      prospectId: this.outreachModel.prospectId,
      channel: this.outreachModel.channel,
      result: this.outreachModel.result,
      lastTouchAtUtc: this.outreachModel.lastTouchAtUtc || null,
      evidenceJson: this.outreachModel.evidenceJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.outreachOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveDisavow(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.createDisavow({
      searchPropertyId: this.selectedPropertyId,
      entity: this.disavowModel.entity,
      entityKind: this.disavowModel.entityKind,
      reason: this.disavowModel.reason
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.disavowOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  createProspectTask(row: SearchLinkProspectDto): void {
    this.busy.set(true);
    this.api.createTaskFromProspect(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  submitDisavow(row: SearchDisavowItemDto): void {
    this.busy.set(true);
    this.api.submitDisavow(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties[0]) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoards();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadBoards(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    const propertyId = this.selectedPropertyId;
    forkJoin({
      prospects: this.api.listProspects(propertyId),
      outreach: this.api.listOutreach(propertyId),
      funnel: this.api.getOutreachFunnel(propertyId),
      backlinks: this.api.listBacklinks(propertyId),
      disavow: this.api.listDisavow(propertyId)
    }).subscribe({
      next: boards => {
        this.prospects.set(boards.prospects);
        this.outreach.set(boards.outreach);
        this.funnel.set(boards.funnel);
        this.backlinks.set(boards.backlinks);
        this.disavow.set(boards.disavow);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
